// Single source of truth for WHICH convorcher environment this build talks to.
//
// ENG-901: the deployed gass bundle used convorcher-STAGING for `/api/*` but
// convorcher-PRODUCTION for the WebSocket. Nobody configured that — the API
// target and the WS URL were two independent settings whose two independent
// in-code defaults simply named different environments, so a build that set
// neither straddled both. (Vite silently ignores an unknown `VITE_*`, and the
// docs advertised a renamed-away name, so the split went unnoticed for months —
// see tests/unit/envVarNames.test.mjs.)
//
// The fix is structural, not a corrected constant: the WS URL is DERIVED from
// the API target, so one value picks the environment and both transports follow.
// A split is no longer representable by accident — only deliberately, via an
// explicit VITE_CONVORCHER_WS_URL.
//
// Resolution order:
//   API target  1. VITE_CONVORCHER_API_TARGET   2. staging (the npm run dev default)
//   WS URL      1. VITE_CONVORCHER_WS_URL       2. derived from the API target
//
// The explicit WS override exists for one real case: `npm run dev:local` runs
// the API against the local Go stack, whose own `/ws` is a no-op stub (local-dev
// only mocks DB + S3), so a dev may want a REMOTE staging WS alongside a local
// API. That's a deliberate split and stays expressible.
//
// Pure and env-injected (no `import.meta.env`, no side effects) so both the
// browser modules and vite.config.js can use it, and so it's unit-testable
// under plain node — see tests/unit/convorcherTargets.test.mjs.

export const CONVORCHER_API_TARGET_DEFAULT = 'https://convorcher-staging.avataros.xyz'

// The route convorcher serves the bsstream WebSocket on.
const WS_PATH = '/ws'

const PROTOCOL_TO_WS = { 'https:': 'wss:', 'http:': 'ws:' }

export function resolveConvorcherApiTarget(env = {}) {
  return trimOrEmpty(env.VITE_CONVORCHER_API_TARGET) || CONVORCHER_API_TARGET_DEFAULT
}

export function resolveConvorcherWsUrl(env = {}) {
  const explicit = trimOrEmpty(env.VITE_CONVORCHER_WS_URL)
  return explicit || deriveWsUrlFromApiTarget(resolveConvorcherApiTarget(env))
}

// The URL a RUNNING build should actually connect to (ENG-1009).
//
// Deployed builds connect SAME-ORIGIN: nginx's `location = /ws` forwards to
// ${CONVORCHER_URL} at RUNTIME, exactly like `/api/` already does — so one
// Coolify env var picks the convorcher environment for both transports and
// the image bakes no environment at all (build once, promote the artifact;
// the promotion pipeline re-tags images, it never rebuilds them). Before
// this, the WS target was baked at build time, which pinned production gass
// to staging convorcher no matter what the runtime env said.
//
// Dev builds keep the DIRECT cross-origin connection derived from the API
// target: the Vite dev server has no /ws proxy, and the session cookie
// attaches cross-site (Domain=.avataros.xyz) just as it does for /auth.
//
// An explicit VITE_CONVORCHER_WS_URL still beats everything — the deliberate
// dev:local split (local API + remote staging WS) stays expressible.
//
// `dev` and `locationOrigin` are injected (never read from import.meta here)
// so this stays pure and node-testable like the rest of the module.
export function resolveRuntimeWsUrl(env = {}, { dev = false, locationOrigin = '' } = {}) {
  const explicit = trimOrEmpty(env.VITE_CONVORCHER_WS_URL)
  if (explicit) return explicit
  if (dev) return deriveWsUrlFromApiTarget(resolveConvorcherApiTarget(env))
  const origin = trimOrEmpty(locationOrigin)
  if (!origin) {
    // A deployed SPA always has a window origin; reaching this means a
    // non-browser context asked for a runtime URL it cannot have. Loud
    // failure beats deriving a plausible-but-wrong cross-origin endpoint
    // (the exact silent-split failure ENG-901 documents above).
    throw new Error(
      'resolveRuntimeWsUrl: no locationOrigin in a non-dev context — ' +
      'deployed builds derive the WS URL from the page origin (nginx /ws proxies it)',
    )
  }
  return deriveWsUrlFromApiTarget(origin)
}

// `https://host[:port]` -> `wss://host[:port]/ws` (and http -> ws, so a local
// stack on :8080 works). Only the ORIGIN of the API target is used: any path on
// it (`/`, `/api`, an already-present `/ws`) is dropped rather than concatenated,
// so the route can't double up.
//
// Throws on anything that isn't an http(s) URL. A malformed target is a config
// error, and per the repo's UX rules a loud failure beats deriving a plausible
// but wrong endpoint and silently failing to connect at runtime.
export function deriveWsUrlFromApiTarget(apiTarget) {
  const raw = trimOrEmpty(apiTarget)
  let url
  try {
    url = new URL(raw)
  } catch {
    throw new Error(
      `convorcher API target is not a valid URL: ${JSON.stringify(apiTarget)} — ` +
        `expected e.g. ${CONVORCHER_API_TARGET_DEFAULT}`
    )
  }

  const wsProtocol = PROTOCOL_TO_WS[url.protocol]
  if (!wsProtocol) {
    throw new Error(
      `convorcher API target must be http(s), got ${url.protocol} in ${JSON.stringify(apiTarget)}`
    )
  }

  return `${wsProtocol}//${url.host}${WS_PATH}`
}

function trimOrEmpty(v) {
  return typeof v === 'string' ? v.trim() : ''
}
